import { Account } from '@radixdlt/radix-dapp-toolkit';
import { LoaderFunctionArgs } from 'react-router';
import { Link, useFetcher, useLoaderData, useNavigate } from 'react-router';
import BigNumber from 'bignumber.js';
import { useEffect, useState } from 'react';
import AccountSelector from '~/components/account-selector';
import PopoverHover from '~/components/popover';
import TokenInput from '~/components/token-input';
import TokenRow from '~/components/token-row';
import TokenSelector from '~/components/token-selector';
import TransactionButton from '~/components/transaction-button';
import { trackEvent } from '~/lib/analytics';
import { fetchPrices } from '~/lib/astrolescent';
import { setDbFromContext } from '~/lib/db';
import { generateCreatePairManifest } from '~/lib/manifests';
import { getPairByBaseToken } from '~/lib/models/pair';
import { getToken } from '~/lib/models/tokens';
import { Token } from '~/lib/types';
import { QUOTE_TOKEN_RESOURCE_ADDRESS, MINIMUM_DFP2_AMOUNT, XRD_RESOURCE_ADDRESS, bigZero, QUOTE_TOKEN_SYMBOL, classNames, DEFAULT_PAIR_FEE } from '~/lib/util';
import { fetchTokensDetails } from '~/rdt/gateway';
import { useRadix } from '~/rdt/radixProvider';
import { loader as walletLoader } from '~/routes/wallet.$account';

BigNumber.config({
   DECIMAL_PLACES: 18,
   EXPONENTIAL_AT: 18,
});

export async function loader({ request, params, context }: LoaderFunctionArgs) {
   setDbFromContext(context);

   const url = new URL(request.url);
   const baseAddress = url.searchParams.get('baseToken');

   const [quoteToken] = await getToken(QUOTE_TOKEN_RESOURCE_ADDRESS);

   if (!baseAddress) {
      return ({
         error: null,
         pairExist: false,
         baseToken: quoteToken,
         quoteToken,
         defaultp0: '',
         dfp2PriceXRD: 0,
      });
   }

   // fetch token from Gateway, since it is not yet in our database
   const [baseToken] = await fetchTokensDetails([baseAddress], true);

   if (!baseToken) {
      return ({
         error: 'Base token does not exist.',
         pairExist: false,
         baseToken: quoteToken,
         quoteToken,
         defaultp0: '',
         dfp2PriceXRD: 0
      });
   }

   if (baseToken.divisibility !== 18) {
      return ({
         error: 'Only tokens with a divisibility of 18 digits are supported.',
         pairExist: false,
         baseToken: baseToken,
         quoteToken,
         defaultp0: '',
         dfp2PriceXRD: 0,
      });
   }

   // check if the pair already exists.
   const [row] = await getPairByBaseToken(baseAddress);

   if (row) {
      return ({
         error: null,
         pairExist: true,
         baseToken,
         quoteToken,
         defaultp0: '',
         dfp2PriceXRD: 0
      });
   }

   // fetch prices to calculate p0
   const prices = await fetchPrices();
   const dfp2PriceXRD = prices[QUOTE_TOKEN_RESOURCE_ADDRESS]?.tokenPriceXRD;

   let p0 = bigZero;
   if (!!prices[baseAddress] && !!dfp2PriceXRD) {
      p0 = new BigNumber(prices[baseAddress].tokenPriceXRD).div(dfp2PriceXRD);
   }

   return ({
      error: null,
      pairExist: false,
      baseToken,
      quoteToken,
      defaultp0: p0.toFixed(baseToken.divisibility || 18),
      dfp2PriceXRD
   });
}

export default function LiquidityCreate() {
   const { baseToken, quoteToken, defaultp0, pairExist, error, dfp2PriceXRD } = useLoaderData<typeof loader>();

   const [tokens, setTokens] = useState<Token[]>([]);
   const [p0, setP0] = useState(defaultp0);
   const [fee, setFee] = useState(DEFAULT_PAIR_FEE);
   const [baseTokensRequired, setBaseTokensRequired] = useState('');

   const { sendTransaction, transaction } = useRadix();
   const [account, setAccount] = useState<string | null>(null);
   const walletFetcher = useFetcher<typeof walletLoader>();
   const navigate = useNavigate();

   function onAccount(account: Account) {
      setAccount(account.address);

      walletFetcher.load(`/wallet/${account.address}`);
   }

   useEffect(() => {
      if (!walletFetcher.data) {
         return;
      }

      walletFetcher.data.sort((a: Token, b: Token) => {
         if (a.address === XRD_RESOURCE_ADDRESS) {
            return -1;
         } else if (b.address === XRD_RESOURCE_ADDRESS) {
            return 1;
         } else if (a.name < b.name) {
            return -1;
         } else if (a.name > b.name) {
            return 1;
         }
      });

      setTokens(walletFetcher.data);

      if (!baseToken) {
         onBaseToken(walletFetcher.data[0]);
      }
   }, [walletFetcher.data]);

   useEffect(() => {
      onP0Change(defaultp0);
   }, [baseToken]);

   function onBaseToken(token: Token) {
      navigate(`?baseToken=${token.address}`);
   }

   function onP0Change(p0String: string) {
      const p0 = parseFloat(p0String);
      const minimum = MINIMUM_DFP2_AMOUNT;

      setP0(p0String);

      if (!isNaN(p0) && p0 > 0) {
         const needed = new BigNumber(minimum).div(p0).toFixed(baseToken.divisibility || 18);
         setBaseTokensRequired(needed);   
      }
   }

   function onBasePriceChange(price: string) {
      const p = parseFloat(price);

      console.log(price, dfp2PriceXRD, p);


      if (!isNaN(p) && p > 0) {
         const priceiInDFP2 = new BigNumber(p).div(dfp2PriceXRD).toFixed(baseToken.divisibility || 18);
         onP0Change(priceiInDFP2);
      }
   }

   async function onCreatePair() {
      if (!account) {
         return;
      }

      const transactionManifest = generateCreatePairManifest(account, baseToken.address, baseTokensRequired, p0, fee);

      sendTransaction(transactionManifest, `Create ${baseToken.symbol}/${QUOTE_TOKEN_SYMBOL} pool on DefiPlaza`);

      trackEvent("send_tx", {
         type: "createPool",
      });
   }

   return (
     <div className="mx-4 mb-8 xl:mx-auto xl:w-3/4">
       <div className="flex items-center justify-between">
         <h1 className="py-4 text-left text-3xl font-bold lg:p-8 lg:text-4xl">Create new pool</h1>
       </div>

       <section className="lg:items-top flex flex-col-reverse lg:flex-row lg:space-x-4">
         <div className="grow space-y-4">
           <AccountSelector mode="light" onAccount={onAccount}></AccountSelector>

           <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
             <div className="flex items-center justify-between">
               <label htmlFor="tokenInput" className="text-sm text-purple-800">
                 Select tokens
               </label>
             </div>
             <div className="flex flex-wrap items-center space-x-4">
               <TokenSelector name="inputToken" tokens={tokens} selected={baseToken} onSelected={onBaseToken} />
               <TokenRow token={quoteToken} />
             </div>
           </section>

           {error ? <p className="shadow-xs text-md mt-2 rounded-md bg-red-800 px-4 py-1 text-center text-base font-medium text-white">{error}</p> : null}

           {pairExist ? (
             <section className="rounded-xl bg-white p-4 text-center text-black lg:rounded-2xl lg:p-8">
               The {baseToken.symbol}/{quoteToken.symbol} pool already exists, you can{" "}
               <Link to={`../add/${baseToken.address}`} className="text-purple-800">
                 add liquidity
               </Link>{" "}
               instead.
             </section>
           ) : null}

           {transaction?.intentHash ? (
             <section className="rounded-xl bg-white p-4 text-center text-black lg:rounded-2xl lg:p-8">
               The {baseToken.symbol}/{quoteToken.symbol} pool has been created, you can now{" "}
               <Link to={`../add/${baseToken.address}`} className="text-purple-800">
                 add liquidity
               </Link>
               .
             </section>
           ) : null}

           {!pairExist && !error && !transaction?.intentHash ? (
             <>
               <section className="mt-8 flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
                 <label htmlFor="tokenInput" className="text-sm text-purple-800">
                   Trading fee
                 </label>
                 <span className="isolate inline-flex rounded-md text-black">
                   <button
                     type="button"
                     onClick={() => setFee(0.004)}
                     className={classNames(fee == 0.004 ? "ring-primary-500 ring-2" : "ring-1 ring-gray-300", "relative grow rounded-l-md bg-white px-3 py-2 ring-inset hover:bg-gray-50 focus:z-10")}
                   >
                     <span>
                       <p className="text-primary-500 text-2xl">0.4%</p>
                       <p>Best for project tokens</p>
                     </span>
                   </button>

                   <button
                     type="button"
                     onClick={() => setFee(0.01)}
                     className={classNames(fee !== 0.004 ? "ring-primary-500 ring-2" : "ring-1 ring-gray-300", "relative grow rounded-r-md bg-white px-3 py-2 ring-inset hover:bg-gray-50 focus:z-10")}
                   >
                     <span>
                       <p className="text-primary-500 text-2xl">1%</p>
                       <p>Best for meme coins</p>
                     </span>
                   </button>
                 </span>
               </section>

               <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
                 <div className="flex items-center justify-between">
                   <label htmlFor="tokenInput" className="text-sm text-purple-800">
                     <PopoverHover title={`Price of ${baseToken?.symbol || "base token"} in ${QUOTE_TOKEN_SYMBOL}`}>
                       <div className="w-40 space-y-2 lg:w-60">
                         <p>
                           You can calculate the price of {baseToken?.symbol} in {QUOTE_TOKEN_SYMBOL} by dividing the XRD price of {baseToken?.symbol} by the XRD price of {QUOTE_TOKEN_SYMBOL}.
                         </p>
                         <p>You can also fill in the price of {baseToken?.symbol} in XRD below and we calculate the right price for you:</p>
                         <p className="border-primary-200 flex items-center border px-1">
                           <input
                             type="text"
                             onChange={(evt: React.ChangeEvent<HTMLInputElement>) => onBasePriceChange((evt.target as HTMLInputElement).value)}
                             placeholder="0.00"
                             autoComplete="off"
                             spellCheck="false"
                             className="mr-4 flex-auto border-none bg-transparent p-0 text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                           />
                           XRD
                         </p>
                         <p>
                           Price in {QUOTE_TOKEN_SYMBOL}: {p0}
                         </p>
                       </div>
                     </PopoverHover>
                   </label>
                 </div>
                 <div className="flex flex-wrap items-center justify-between">
                   <TokenInput value={p0} onChange={onP0Change} />
                   <span className="text-2xl font-medium text-black">{QUOTE_TOKEN_SYMBOL}</span>
                 </div>
               </section>

               {/* <Disclosure as="section" className="flex flex-col bg-white rounded-xl lg:rounded-2xl px-4 py-2 lg:px-8 lg:py-6">
                  {({ open }) => (
                     <>
                        <dt>
                           <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-700">
                              <span className="text-xl font-semibold leading-7">Advanced settings</span>
                              <span className="ml-6 flex h-7 items-center">
                                 {open ? <ChevronUpIcon className="h-6 w-6" aria-hidden="true" /> : <ChevronDownIcon className="h-6 w-6" aria-hidden="true" />}
                              </span>
                           </Disclosure.Button>
                        </dt>
                        <Disclosure.Panel as="div" className="mt-2 grid grid-cols-3 gap-4">
                           <div>
                              <div className="flex items-center">
                                 <label htmlFor="tokenInput" className="grow text-sm text-purple-800 text-right">
                                    Fee
                                 </label>
                              </div>
                              <div className="flex justify-end items-end">
                                 <input
                                    type="text"
                                    defaultValue={0.3}
                                    placeholder="0.00"
                                    autoComplete="off"
                                    spellCheck="false"
                                    className="w-40 bg-transparent border-none p-0 text-4xl text-right text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                                 />
                                 <span className="text-2xl text-black font-medium">%</span>
                              </div>
                           </div>

                           <div>
                              <div className="flex justify-between items-center">
                                 <label htmlFor="tokenInput" className="grow text-sm text-purple-800 text-right">
                                    Concentration In
                                 </label>
                              </div>
                              <div className="flex justify-end items-end">
                                 <input
                                    type="text"
                                    defaultValue={2.5}
                                    placeholder="0.00"
                                    autoComplete="off"
                                    spellCheck="false"
                                    className="w-40 bg-transparent border-none p-0 text-4xl text-right text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                                 />
                                 <span className="text-2xl text-black font-medium">x</span>
                              </div>
                           </div>

                           <div>
                              <div className="flex justify-between items-center">
                                 <label htmlFor="tokenInput" className="grow text-sm text-purple-800 text-right">
                                    Concentration Out
                                 </label>
                              </div>
                              <div className="flex justify-end items-end">
                                 <input
                                    type="text"
                                    defaultValue={1}
                                    placeholder="0.00"
                                    autoComplete="off"
                                    spellCheck="false"
                                    className="w-40 bg-transparent border-none p-0 text-4xl text-right text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                                 />
                                 <span className="text-2xl text-black font-medium">x</span>
                              </div>
                           </div>
                        </Disclosure.Panel>
                     </>
                  )}
               </Disclosure> */}

               <p className="flex justify-between px-8 py-4">
                 <svg className="h-6 w-6" viewBox="8 8 24 24" xmlns="http://www.w3.org/2000/svg">
                   <path
                     d="M29.088 21.972c.432-.443.432-1.212 0-1.654a1.128 1.128 0 0 0-1.617 0l-6.322 6.466V10.58c0-.645-.513-1.17-1.144-1.17-.63 0-1.143.525-1.143 1.17v16.203l-6.321-6.466a1.128 1.128 0 0 0-1.618 0 1.178 1.178 0 0 0-.335.827c0 .312.12.606.336.827l7.964 8.143a1.556 1.556 0 0 0 2.237 0l7.963-8.143z"
                     fill="currentColor"
                     fillRule="nonzero"
                   ></path>
                 </svg>
               </p>

               <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
                 <div className="flex items-center justify-between">
                   <label htmlFor="tokenInput" className="text-sm text-purple-800">
                     Base Tokens
                   </label>
                 </div>
                 <div className="flex flex-wrap items-center justify-between">
                   <TokenInput value={baseTokensRequired} onChange={() => {}} />

                   <TokenRow token={baseToken} />
                 </div>
               </section>

               <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
                 <div className="flex items-center justify-between">
                   <label htmlFor="tokenInput" className="text-sm text-purple-800">
                     Quote Tokens
                   </label>
                 </div>
                 <div className="flex flex-wrap items-center justify-between">
                   <TokenInput value={"" + MINIMUM_DFP2_AMOUNT} onChange={() => {}} />

                   <TokenRow token={quoteToken} />
                 </div>
               </section>

               <TransactionButton title="Create New Pool" onClick={onCreatePair} />
             </>
           ) : null}
         </div>

         <div className="dark:bg-background-900 flex flex-col space-y-4 rounded-xl bg-white p-8 lg:w-96 lg:rounded-2xl">
           <p className="text-primary-700 dark:text-primary-100">Creating a new pair</p>
           <p>
             When creating a new pool an amount of base tokens and {QUOTE_TOKEN_SYMBOL} must be provided which will be <strong className="text-primary-700 dark:text-primary-100">donated</strong> to
             the protocol.
           </p>
           <p>This minimum amount of liquidity is used to prevent the pool from ever running empty, which could lead to price manipulation.</p>
           <p>
             After creating the pair, you can <Link to="/liquidity/add">add liquidity</Link>.
           </p>

           <p className="text-primary-700 dark:text-primary-100">Please note</p>
           <p>There can be only one pool per base token.</p>
           <p>It is not possible to set the fee and concentration levels of new pools. This might be added in the future. Pools that are created are not updateable.</p>

           <p className="text-primary-700 dark:text-primary-100">Pool governance</p>
           <p>The DefiPlaza DAO can delist pools that might harm the normal operation of the DEX. Examples are:</p>
           <ul className="list-disc pl-5">
             <li>Pools created with a price of the base token in {QUOTE_TOKEN_SYMBOL} which is far away from the market price, to prevent price manipulation.</li>
             <li>Pools with base tokens that are considered a scam.</li>
           </ul>
           <p>Pools that are delisted can still be used by interacting with them directly outside of the DefiPlaza UI. Delisting only removes them from the UI and the main DEX component.</p>
         </div>
       </section>
     </div>
   );
}
